import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Search, Loader2, IdCard, BarChart3 } from 'lucide-react';
import { getPaidAssessments, getAssessmentById, getUsers, getUsersForFranchise, getResultForCandidateAssessment, getRecording, getTranscript } from '../../services/apiclient';
import { useRbac } from '../../context';
import ResultSidebarForCandidate from '../resultsidebarForAssessment';
import { toast } from 'sonner';
import UserDetailSidebar from '../../components/assessments/UserDetailSidebar'; 

const LIMIT = 10;

const AssessmentCandidates = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [userAssessments, setUserAssessments] = useState<any>([]);
  const [page, setPage] = useState(1);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false); // For fetchCandidates
  const [loadingCandidates, setLoadingCandidates] = useState<Record<string, boolean>>({}); // For per-candidate loading
  const [hasMore, setHasMore] = useState(true);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const { user } = useRbac();
  const observer = useRef<IntersectionObserver | null>(null);

  const lastCandidateRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          setPage(prev => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => {
    const fetchCandidates = async () => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: LIMIT,
          searchQuery: searchTerm,
          role: 'candidate',
        };
        const response = user?.role === 'ADMIN'
          ? await getUsers(params)
          : ['FBDE','FREELANCE_COUNSELLOR'].includes(user?.role) && await getUsersForFranchise({ ...params, franchiseId: user?.franchiseId || user.userId });

        if (!response.success) {
          throw new Error(response.message);
        }

        setCandidates(prev => {
          if (page === 1) return response.data.users;
          return [...prev, ...response.data.users];
        });
        setHasMore(response.data.users.length === LIMIT);
      } catch (error) {
        toast.error("An error occurred while fetching candidates. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [page, searchTerm, user]);

  useEffect(() => {
    setPage(1);
    setCandidates([]);
    setHasMore(true);
  }, [searchTerm]);

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-50';
    if (score >= 75) return 'text-blue-600 bg-blue-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const handleViewDetails = (candidate: any) => {
    setSelectedCandidate(candidate);
    setShowUserDetails(true);
  };

  const handleViewInterviews = async (id: string, candidate: any) => {
    setLoadingCandidates(prev => ({ ...prev, [id]: true }));
    try {
      const response = await getPaidAssessments(id);
      if (!response.success) {
        throw new Error(response.message);
      }

      const assessmentsdata = await Promise.all(
        response.data.map((assessment: any) =>
          getAssessmentById(assessment.assessmentId)
        )
      );

      const combinedAssessments = await Promise.all(
        response.data.map(async (paidAssessment: any) => {
          const matchingAssessment = assessmentsdata.find(
            (assessmentData) =>
              assessmentData.data.data.assessment._id === paidAssessment.assessmentId
          );

          let result, recording, transcript;
          try {
            result = await getResultForCandidateAssessment(paidAssessment.interviewId);
            recording = await getRecording(paidAssessment.interviewId);
            transcript = await getTranscript(paidAssessment.interviewId);
          } catch (error) {
            console.error(`Error fetching data for interview ${paidAssessment.interviewId}:`, error);
            result = null;
            recording = null;
            transcript = [];
          }

          if (matchingAssessment) {
            return {
              id: paidAssessment._id,
              assessmentId: paidAssessment.assessmentId,
              assessmentIdVelox: paidAssessment.assessmentIdVelox,
              assessmentLink: paidAssessment.assessmentLink,
              candidateId: paidAssessment.candidateId,
              interviewId: paidAssessment.interviewId,
              linkExpiryTime: paidAssessment.linkExpiryTime,
              createdAt: paidAssessment.createdAt,
              title: matchingAssessment.data.data.assessment.title,
              description: matchingAssessment.data.data.assessment.description,
              category: matchingAssessment.data.data.assessment.category,
              difficulty: matchingAssessment.data.data.assessment.difficulty,
              timeLimit: matchingAssessment.data.data.assessment.timeLimit,
              isPremium: matchingAssessment.data.data.assessment.isPremium,
              pricing: matchingAssessment.data.data.assessment.pricing,
              passingScore: matchingAssessment.data.data.assessment.passingScore,
              tags: matchingAssessment.data.data.assessment.tags,
              result,
              recording: recording?.data || null,
              transcript: transcript?.data || [],
              firstName: candidate.firstName,
              lastName: candidate.lastName,
              email: candidate.email,
              status: paidAssessment.status || 0,
              score: result?.data?.report?.score || 0,
              startTime: paidAssessment.startTime,
              endTime: paidAssessment.endTime,
              lastStatusUpdatedTime: paidAssessment.lastStatusUpdatedTime,
            };
          }

          return {
            id: paidAssessment._id,
            assessmentId: paidAssessment.assessmentId,
            assessmentIdVelox: paidAssessment.assessmentIdVelox,
            assessmentLink: paidAssessment.assessmentLink,
            candidateId: paidAssessment.candidateId,
            interviewId: paidAssessment.interviewId,
            linkExpiryTime: paidAssessment.linkExpiryTime,
            createdAt: paidAssessment.createdAt,
            title: "Unknown Assessment",
            description: "No details available",
            category: null,
            difficulty: null,
            timeLimit: null,
            isPremium: false,
            pricing: null,
            passingScore: null,
            tags: [],
            result: null,
            recording: null,
            transcript: [],
            firstName: candidate.firstName,
            lastName: candidate.lastName,
            email: candidate.email,
            status: paidAssessment.status || 0,
            score: 0,
            startTime: paidAssessment.startTime,
            endTime: paidAssessment.endTime,
            lastStatusUpdatedTime: paidAssessment.lastStatusUpdatedTime,
          };
        })
      );

      setUserAssessments(combinedAssessments);
      setSelectedCandidate(candidate);
    } catch (error) {
      toast.error("An error occurred while fetching assessments. Please try again later.");
    } finally {
      setLoadingCandidates(prev => ({ ...prev, [id]: false }));
    }
  };

  return (
    <div className="space-y-6 relative">
      <div className="flex justify-between items-center">
        <div className='pt-6 ml-[25px]'>
          <h1 className="text-3xl font-bold text-gray-900">Candidates</h1>
          <p className="text-gray-600 mt-2">View and manage candidate profiles and assessment results.</p>
        </div>
      </div>

      <Card style={{ border: "none" }}>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Candidates Overview</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search candidates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && candidates.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
              <span className="ml-2 text-gray-600">Loading candidates...</span>
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center text-gray-600 py-10">
              No candidates found.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-6">
              {candidates.map((candidate: any, index) => {
                const isLast = index === candidates.length - 1;
                return (
                  <div
                    key={candidate._id}
                    className="border rounded-xl p-5 hover:shadow-md transition bg-white w-full mx-auto"
                    ref={isLast ? lastCandidateRef : null}
                  >
                    {/* Candidate Info Section */}
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      {candidate.avatar ? (
                        <Avatar className="h-14 w-14 ring-2 ring-gray-100">
                          <AvatarImage src={candidate.avatar} alt={candidate.name} />
                        </Avatar>
                      ) : (
                        <Avatar className="h-14 w-14 ring-2 ring-gray-100">
                          <AvatarFallback className="text-sm font-semibold text-gray-700">
                            {candidate.name?.slice(0, 2).toUpperCase() || "C"}
                          </AvatarFallback>
                        </Avatar>
                      )}

                      {/* Candidate Details */}
                      <div className="flex-1">
                        {/* Top row: Name + Badges */}
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg text-gray-900">
                            {candidate.name}
                          </h3>
                          {candidate.franchiserId && (
                            <Badge variant="outline" className="text-xs">Franchise</Badge>
                          )}
                          {/* <Badge
                            className={`text-xs px-2 py-0.5 rounded-md ${
                              candidate.status !== 0
                                ? candidate.status === 2
                                  ? "bg-green-100 text-green-700"
                                  : "bg-blue-100 text-blue-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {candidate.status !== 0
                              ? candidate.status === 2
                                ? "Selected"
                                : "Completed"
                              : "Pending"}
                          </Badge> */}
                        </div>

                        {/* Contact Info */}
                        <p className="text-sm text-gray-600">{candidate.email}</p>
                        <p className="text-sm text-gray-500">{candidate.location}</p>

                        {/* Score */}
                        <div className="mt-3 flex items-center gap-2">
                          {candidate.averageScore && (
                            <><span className="text-sm font-medium text-gray-700">Average Score:</span>
                          <Badge
                            className={`${getScoreColor(candidate.averageScore)} text-sm px-2 py-0.5 rounded-md`}
                          >
                            {candidate.averageScore || 0}%
                          </Badge>
                            </>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-lg border-gray-300 text-gray-700"
                            onClick={() => handleViewDetails(candidate)}
                          >
                            <IdCard className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-lg border-[#EC6E51] text-[#EC6E51]"
                            onClick={() => handleViewInterviews(candidate._id, candidate)}
                            disabled={loadingCandidates[candidate._id]}
                          >
                            {loadingCandidates[candidate._id] ? (
                              <span className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading...
                              </span>
                            ) : (
                              <>
                                <BarChart3 className="h-4 w-4 mr-1" />
                                View Assessments
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {loading && candidates.length > 0 && (
                <div className="flex justify-center items-center py-4 col-span-full">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-600" />
                  <span className="ml-2 text-gray-600">Loading more candidates...</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedCandidate && showUserDetails && (
        <UserDetailSidebar
          user={selectedCandidate}
          onClose={() => {
            setSelectedCandidate(null);
            setShowUserDetails(false);
          }}
        />
      )}
      {userAssessments.length > 0 && (
        <ResultSidebarForCandidate
          userAssessments={userAssessments}
          selectedCandidate={selectedCandidate}
          onClose={() => setUserAssessments([])}
        />
      )}
    </div>
  );
};

export default AssessmentCandidates;